import { ApiProperty } from '@nestjs/swagger'
import {
  UserStatus,
  Theme,
  ConsentKind,
  RelationType,
  ErasureStatus,
} from '../enums'
import type { UserDetail, UserConsent, UserPreference, UserRelation, DataErasureRequest, UserSummary } from '../types/user-contracts.types'

/**
 * DTO de réponse pour un utilisateur complet (Profil public/privé)
 */
export class UserResponseDto implements UserDetail {
  @ApiProperty({ description: 'ID utilisateur' })
  id!: string

  @ApiProperty({ description: 'Email utilisateur', required: false, nullable: true })
  email?: string | null

  @ApiProperty({ description: 'Username', required: false, nullable: true })
  username?: string | null

  @ApiProperty({ description: 'Prénom', required: false, nullable: true })
  firstName?: string | null

  @ApiProperty({ description: 'Nom de famille', required: false, nullable: true })
  lastName?: string | null

  @ApiProperty({ description: 'Nom affiché', required: false, nullable: true })
  displayName?: string | null

  @ApiProperty({ description: 'URL avatar', required: false, nullable: true })
  avatarUrl?: string | null

  @ApiProperty({ description: 'ID établissement', required: false, nullable: true })
  establishmentId?: string | null

  @ApiProperty({ description: 'Locale' })
  locale!: string

  @ApiProperty({ description: 'Timezone', required: false, nullable: true })
  timezone?: string | null

  @ApiProperty({ description: 'Thème', enum: ['light', 'dark', 'system'] })
  theme!: string | Theme

  @ApiProperty({ description: 'Date de naissance', required: false, nullable: true })
  birthDate?: string | Date | null

  @ApiProperty({ description: 'Genre', required: false, nullable: true })
  gender?: string | null

  @ApiProperty({ description: 'Langues parlées', type: [String] })
  spokenLanguages!: string[]

  @ApiProperty({ description: 'Paramètres', type: Object })
  settings!: Record<string, any>

  @ApiProperty({ description: 'Métadonnées', type: Object })
  metadata!: Record<string, any>

  @ApiProperty({ description: 'Statut du compte', enum: UserStatus })
  status!: UserStatus

  @ApiProperty({ description: 'Date de suspension', required: false, nullable: true })
  suspendedAt?: string | Date | null

  @ApiProperty({ description: 'Date de désactivation', required: false, nullable: true })
  deactivatedAt?: string | Date | null

  @ApiProperty({ description: 'Date de dernière connexion', required: false, nullable: true })
  lastSeenAt?: string | Date | null

  @ApiProperty({ description: 'Date de suppression', required: false, nullable: true })
  deletedAt?: string | Date | null

  @ApiProperty({ description: 'Date de création' })
  createdAt!: string | Date

  @ApiProperty({ description: 'Date de dernière mise à jour' })
  updatedAt!: string | Date
  
  // Counts
  @ApiProperty({ description: 'Nombre de consentements', required: false })
  consentsCount?: number

  @ApiProperty({ description: 'Nombre de préférences', required: false })
  preferencesCount?: number

  @ApiProperty({ description: 'Nombre de relations', required: false })
  relationsCount?: number
}

export class UsersResponseDto {
  @ApiProperty({ description: 'Liste des utilisateurs', type: [UserResponseDto] })
  users!: UserResponseDto[]
}

/**
 * DTO de réponse pour un consentement
 */
export class ConsentResponseDto implements UserConsent {
  @ApiProperty()
  id!: string

  @ApiProperty()
  userId!: string

  @ApiProperty({ enum: ConsentKind })
  kind!: ConsentKind

  @ApiProperty()
  version!: string

  @ApiProperty()
  isGranted!: boolean

  @ApiProperty({ nullable: true })
  grantedAt!: string | Date | null

  @ApiProperty({ required: false, nullable: true })
  withdrawnAt?: string | Date | null

  @ApiProperty({ required: false, nullable: true })
  ipAddress?: string | null

  @ApiProperty({ required: false, nullable: true })
  userAgent?: string | null

  @ApiProperty({ required: false, nullable: true })
  localeAtSign?: string | null
}

/**
 * DTO de réponse pour une préférence
 */
export class PreferenceResponseDto implements UserPreference {
  @ApiProperty()
  id!: string

  @ApiProperty()
  userId!: string

  @ApiProperty()
  key!: string

  @ApiProperty()
  value!: any

  @ApiProperty()
  createdAt!: string | Date

  @ApiProperty()
  updatedAt!: string | Date
}

/**
 * DTO de réponse pour une relation
 */
export class RelationResponseDto implements UserRelation {
  @ApiProperty()
  id!: string

  @ApiProperty()
  fromUserId!: string

  @ApiProperty()
  toUserId!: string

  @ApiProperty({ enum: RelationType })
  type!: RelationType

  @ApiProperty()
  createdAt!: string | Date

  @ApiProperty()
  updatedAt!: string | Date

  @ApiProperty({ required: false })
  fromUser?: UserSummary

  @ApiProperty({ required: false })
  toUser?: UserSummary
}

/**
 * DTO de réponse pour une demande d'effacement
 */
export class ErasureRequestResponseDto implements DataErasureRequest {
  @ApiProperty()
  id!: string

  @ApiProperty()
  userId!: string

  @ApiProperty({ enum: ErasureStatus })
  status!: ErasureStatus

  @ApiProperty({ nullable: true })
  reason!: string | null

  @ApiProperty()
  requestedAt!: string | Date

  @ApiProperty({ nullable: true })
  deadlineAt!: string | Date | null

  @ApiProperty({ nullable: true })
  processedAt!: string | Date | null

  @ApiProperty({ type: Object })
  details!: Record<string, any>

  @ApiProperty()
  createdAt!: string | Date

  @ApiProperty()
  updatedAt!: string | Date

  @ApiProperty({ required: false, type: Object })
  user?: UserSummary
}

/**
 * DTO pour la vérification de consentement
 */
export class ConsentCheckResponseDto {
  @ApiProperty({ description: 'Si le consentement est accordé' })
  hasConsent!: boolean

  @ApiProperty({ description: 'Le consentement actif s\'il existe', type: ConsentResponseDto, nullable: true })
  consent!: ConsentResponseDto | null
}

/**
 * DTO pour la réponse des préférences utilisateur (map + raw)
 */
export class UserPreferencesResponseDto {
  @ApiProperty({ description: 'Préférences sous forme de clé-valeur' })
  preferences!: Record<string, any>

  @ApiProperty({ description: 'Liste brute des préférences', type: [PreferenceResponseDto] })
  _raw!: PreferenceResponseDto[]
}

/**
 * DTO pour la réponse des relations utilisateur (from/to)
 */
export class UserRelationsResponseDto {
  @ApiProperty({ description: 'Relations où l\'utilisateur est la source', type: [RelationResponseDto] })
  relationsFrom!: RelationResponseDto[]

  @ApiProperty({ description: 'Relations où l\'utilisateur est la cible', type: [RelationResponseDto] })
  relationsTo!: RelationResponseDto[]
}
